import type { SidebarPage } from '@clickguard/design-system'
import { useCallback, useMemo } from 'react'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import BarChartSquareDown from '@untitled-ui/icons-react/build/esm/BarChartSquareDown'
import Calendar from '@untitled-ui/icons-react/build/esm/Calendar'
import CheckCircle from '@untitled-ui/icons-react/build/esm/CheckCircle'
import CoinsHand from '@untitled-ui/icons-react/build/esm/CoinsHand'
import CursorClick01 from '@untitled-ui/icons-react/build/esm/CursorClick01'
import FileCheck02 from '@untitled-ui/icons-react/build/esm/FileCheck02'
import Home03 from '@untitled-ui/icons-react/build/esm/Home03'
import ShieldTick from '@untitled-ui/icons-react/build/esm/ShieldTick'
import ShieldZap from '@untitled-ui/icons-react/build/esm/ShieldZap'
import SlashCircle01 from '@untitled-ui/icons-react/build/esm/SlashCircle01'
import Sliders04 from '@untitled-ui/icons-react/build/esm/Sliders04'
import { useWorkspaceAccess, WorkspaceAccess } from 'hooks/use-workspace-access'
import { useFeatureFlag } from 'services/feature-flags'
import { useAccountStore, useAdminStore, useAssetStore } from 'store'
import { appendMarketingParams } from 'utils/marketing-params'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'

/** The product rail's nav, as data: the DS `Sidebar` takes a flat `pages` list, so
 *  what each hand-written `<NavItem>` did travels with its row instead. */

/** Filter params that survive a page change, carried across by `buildNavPath`. */
const SHARED_PARAMS = [
	'from',
	'to',
	'platforms',
	'adAccounts',
	'campaigns',
	'breadCampaigns'
]

const RESTRICTIONS: Partial<
	Record<WorkspaceAccess, { open: string[]; reason: string }>
> = {
	'not-started': {
		open: ['dashboard'],
		reason: 'Start your free trial first'
	},
	'no-setup': { open: ['dashboard'], reason: 'Complete your setup first' },
	'no-ad-account': {
		open: ['dashboard'],
		reason: 'Connect an ad account first'
	},
	cancelled: {
		open: ['dashboard', 'exclusions', 'blacklist'],
		reason: 'Start a new plan first'
	}
}

const LEAD_FORMS_LOCK = {
	plan: 'Available on Pro and Custom plans; upgrade to access lead form protection.',
	tracking:
		"Please install ClickGuard's tracking code to enable the form protection setup."
}

type PageDef = SidebarPage & {
	/** Where the row navigates, before shared and marketing params are added. */
	path: string
	/** The row's own active test, kept 1:1 with the old `selected` props. */
	isActive: (pathname: string) => boolean
	/** The pageview the old menu fired on click, where it fired one. */
	track?: () => void
}

export const useSidebarPages = () => {
	const account = useAccountStore((s) => s.account)
	const hasLeadForms = useAccountStore((s) => s.hasFeature('lead_forms'))
	const asset = useAssetStore((s) => s.asset)
	const demoMode = useAssetStore((s) => s.demoMode)
	const adAccounts = useAssetStore((s) => s.adAccounts)
	const impersonation = useAdminStore((s) => s.impersonation)

	const navigate = useNavigate()
	const location = useLocation()
	const { pathname, search } = location
	// Exact match: the dashboard is the asset root, so a string compare against a path
	// built from the store would miss while the URL's ids and the store's differ.
	const dashboardMatch = useMatch('/workspace/:accountId/asset/:assetId')

	const refundsEnabled = useFeatureFlag('refunds-agent-enabled')
	const leadFormsEnabled = useFeatureFlag('form-protection-enabled')
	const customRulesV2Enabled = useFeatureFlag('custom_rules_v2')
	const { access } = useWorkspaceAccess()
	const trackPostHogEvent = useTrackPostHogEvent()

	const accountId =
		demoMode && !account?.id
			? 'workspace-demo'
			: account?.id || 'workspace-demo'
	const assetId =
		demoMode && !impersonation ? 'website-demo' : asset?.id || 'website-demo'

	const base = `/workspace/${accountId}/asset/${assetId}`
	const navigationRestricted = assetId === 'website-demo' || access !== 'full'
	const hasAdAccount = adAccounts.length > 0
	const leadFormsLock =
		assetId === 'website-demo'
			? null
			: !hasLeadForms
			? 'plan'
			: !asset?.tc_first_installed_at
			? 'tracking'
			: null

	const buildNavPath = useCallback(
		(targetPath: string): string => {
			if (search) {
				const source = new URLSearchParams(search)
				const shared = new URLSearchParams()
				source.forEach((value, key) => {
					if (SHARED_PARAMS.includes(key)) shared.set(key, value)
				})
				const result = shared.toString()
				return appendMarketingParams(
					result ? `${targetPath}?${result}` : targetPath
				)
			}
			return appendMarketingParams(targetPath)
		},
		[search]
	)

	const pages = useMemo<PageDef[]>(() => {
		const list: PageDef[] = [
			{
				id: 'dashboard',
				label: 'Dashboard',
				icon: Home03,
				scope: 'reporting',
				path: base,
				isActive: () => !!dashboardMatch,
				track: () =>
					trackPostHogEvent(
						PostHogEvent.PAGEVIEW_DASHBOARD,
						PostHogPath.DASHBOARD,
						null
					)
			},
			{
				id: 'analytics',
				label: 'Analytics',
				icon: BarChartSquareDown,
				scope: 'reporting',
				path: `${base}/analytics-reports`,
				disabled: navigationRestricted,
				// The rail's own chip (DS `SidebarPage.isNew`), which replaces the
				// hand-placed `<Chip>New</Chip>` the old asset menu carried.
				isNew: true,
				isActive: (p) => p === `${base}/analytics-reports`,
				track: () =>
					trackPostHogEvent(
						PostHogEvent.PAGEVIEW_ANALYTICS,
						PostHogPath.ANALYTICS,
						null
					)
			},
			{
				id: 'clicks',
				label: 'Click Forensics',
				icon: CursorClick01,
				scope: 'reporting',
				path: `${base}/clicks`,
				isActive: (p) => p === `${base}/clicks`,
				track: () =>
					trackPostHogEvent(
						PostHogEvent.PAGEVIEW_CLICKS,
						PostHogPath.CLICKS,
						null
					)
			},
			{
				id: 'reports',
				label: 'Scheduled Reports',
				icon: Calendar,
				scope: 'reporting',
				path: `${base}/reports`,
				disabled: navigationRestricted,
				isActive: (p) => p === `${base}/reports`,
				track: () =>
					trackPostHogEvent(
						PostHogEvent.PAGEVIEW_SCHEDULED_REPORTS,
						PostHogPath.SCHEDULED_REPORTS,
						null
					)
			}
		]

		// DEV-2422 W13 — the old combined row goes the moment the flag is on, replaced by
		// the two rows below that split it. Kept behind the same flag rather than deleted
		// so a flag-off workspace still has a way into its protection settings; the row and
		// the page it points at come out for good when the flag is retired.
		if (!customRulesV2Enabled) {
			list.push({
				id: 'protection',
				label: 'AI + Custom Rules',
				icon: ShieldZap,
				scope: 'protection',
				path: `${base}/protection`,
				disabled: !demoMode && !hasAdAccount,
				// Partial, not exact: the rules pages nest under /protection.
				isActive: (p) => p.includes('/protection'),
				track: () =>
					trackPostHogEvent(
						PostHogEvent.PAGEVIEW_PROTECTION,
						PostHogPath.PROTECTION,
						null
					)
			})
		}

		// DEV-2422. Same flag and same cutover as Custom Rules below: the two pages are the
		// halves of one split and shipping either alone leaves a half-migrated nav.
		if (customRulesV2Enabled) {
			list.push({
				id: 'ai-protection',
				label: 'AI Protection',
				icon: ShieldTick,
				scope: 'protection',
				path: `${base}/ai-protection`,
				// Same gate as the row above: protection needs an ad account to act on.
				disabled: !demoMode && !hasAdAccount,
				isActive: (p) => p === `${base}/ai-protection`,
				track: () =>
					trackPostHogEvent(
						PostHogEvent.PAGEVIEW_AI_PROTECTION,
						PostHogPath.AI_PROTECTION,
						null
					)
			})
		}

		// DEV-2403. Sits BESIDE the old row rather than replacing it — the ticket keeps AI
		// + Custom Rules live and untouched. The old row goes with that ticket's cutover.
		if (customRulesV2Enabled) {
			list.push({
				id: 'custom-rules',
				label: 'Custom Rules',
				icon: Sliders04,
				scope: 'protection',
				path: `${base}/custom-rules`,
				// Same gate as the row above: rules need an ad account to act on.
				disabled: !demoMode && !hasAdAccount,
				// Exact: nothing nests under it, and a partial test would also
				// light this row on the old page's /protection?tab=rules URLs.
				isActive: (p) => p === `${base}/custom-rules`,
				track: () =>
					trackPostHogEvent(
						PostHogEvent.PAGEVIEW_CUSTOM_RULES,
						PostHogPath.CUSTOM_RULES,
						null
					)
			})
		}

		list.push(
			{
				id: 'exclusions',
				label: 'Exclusions',
				icon: CheckCircle,
				scope: 'protection',
				path: `${base}/exclusions`,
				isActive: (p) => p === `${base}/exclusions`,
				track: () =>
					trackPostHogEvent(
						PostHogEvent.PAGEVIEW_EXCLUSIONS,
						PostHogPath.EXCLUSIONS,
						null
					)
			},
			{
				id: 'blacklist',
				label: 'Blacklist',
				icon: SlashCircle01,
				scope: 'protection',
				path: `${base}/blacklist`,
				isActive: (p) => p === `${base}/blacklist`,
				track: () =>
					trackPostHogEvent(
						PostHogEvent.PAGEVIEW_BLACKLIST,
						PostHogPath.BLACKLIST,
						null
					)
			}
		)

		if (refundsEnabled) {
			list.push({
				id: 'refunds',
				label: 'Refunds Agent',
				icon: CoinsHand,
				scope: 'protection',
				path: `${base}/refunds`,
				disabled: navigationRestricted,
				isNew: true,
				newLabel: 'Beta',
				isActive: (p) => p === `${base}/refunds`
			})
		}

		// Cases 2 (no trial yet) and 3 (no ad account) leave only the dashboard;
		// Case 6 (cancelled) also keeps Exclusions and Blacklist. Every other
		// page is off.
		const restriction = RESTRICTIONS[access]
		if (restriction) {
			return list.map((page) =>
				restriction.open.includes(page.id)
					? page
					: { ...page, disabled: true, disabledReason: restriction.reason }
			)
		}

		if (leadFormsEnabled) {
			list.push({
				id: 'lead-forms',
				label: 'Lead Forms',
				icon: FileCheck02,
				scope: 'protection',
				path: `${base}/lead-forms`,
				disabled: leadFormsLock !== null,
				disabledReason: leadFormsLock
					? LEAD_FORMS_LOCK[leadFormsLock]
					: undefined,
				isNew: true,
				isActive: (p) => p === `${base}/lead-forms`
			})
		}

		return list
	}, [
		access,
		base,
		customRulesV2Enabled,
		dashboardMatch,
		demoMode,
		hasAdAccount,
		leadFormsEnabled,
		leadFormsLock,
		refundsEnabled,
		navigationRestricted,
		trackPostHogEvent
	])

	// First match wins, in list order. The old menu evaluated every row
	// independently and could light two at once; the rail has one selection.
	const activeId = useMemo(
		() => pages.find((page) => page.isActive(pathname))?.id,
		[pages, pathname]
	)

	const onSelect = useCallback(
		(id: string) => {
			const page = pages.find((p) => p.id === id)
			if (!page) return
			page.track?.()
			navigate(buildNavPath(page.path))
		},
		[pages, buildNavPath, navigate]
	)

	return { pages, activeId, onSelect }
}
